import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  WithStyles
} from '@material-ui/core';
import { createStyles, Theme, withStyles } from '@material-ui/core/styles';
import { InjectedFormikProps, withFormik } from 'formik';
import * as React from 'react';
import * as Yup from 'yup';
import MyButton from '../../../components/UI/MyButton/MyButton';
import { IUser } from '../../../models/states';
import { UserRoles } from '../../../utilities/utilities';

const styles = (theme: Theme) =>
  createStyles({
    root: {},
    textField: {
      margin: '0',
      marginBottom: '1rem',
      width: '100%'
    },
    selectedListItem: {
      backgroundColor: 'rgba(255, 255, 255, 0.5)'
    },
    checkboxRoot: {
      '&$checked': theme.checkbox.yellow
    },
    checked: {},
    textInput: {
      textTransform: 'capitalize'
    }
  });

interface IFormValues {
  name: string;
  email: string;
  role: string; // select field
  screens: string[]; // make this field single/multiselect based on the user
  status: string; // checkbox
  // jiraUserName: string;
}

interface IFormProps extends WithStyles<typeof styles> {
  user?: IUser;
  onSubmit: (values: any) => void;
  screens: IList[];
  users: IUser[];
}

interface IList {
  label: string;
  value: string;
}

const InnerForm: React.SFC<
  InjectedFormikProps<IFormProps, IFormValues>
> = props => (
  <div>
    <form onSubmit={props.handleSubmit}>
      <div>
        <TextField
          id="name"
          error={Boolean(props.errors.name && props.touched.name)}
          label="Name"
          onChange={props.handleChange}
          value={props.values.name}
          margin="normal"
          className={props.classes.textField}
          helperText={
            props.errors.name && props.touched.name && String(props.errors.name)
          }
          InputLabelProps={{
            shrink: true
          }}
        />
        <TextField
          id="email"
          label="Email"
          className={props.classes.textField}
          value={props.values.email}
          onChange={props.handleChange}
          error={Boolean(props.errors.email && props.touched.email)}
          helperText={
            props.errors.email &&
            props.touched.email &&
            String(props.errors.email)
          }
          margin="normal"
          name="email"
          disabled={props.user ? true : false}
          InputLabelProps={{
            shrink: true
          }}
        />
      </div>
      <FormControl className={props.classes.textField}>
        <InputLabel htmlFor="select-multiple" shrink={true}>
          Role
        </InputLabel>
        <Select
          id="role"
          value={props.values.role}
          className={props.classes.textField}
          onChange={props.handleChange}
          margin="none"
          name="role"
          renderValue={() => (
            <Typography className={props.classes.textInput}>
              {props.values.role}
            </Typography>
          )}
        >
          <MenuItem value={UserRoles.OPERATOR}>
            <Checkbox
              checked={props.values.role === UserRoles.OPERATOR}
              tabIndex={-1}
              disableRipple
              classes={{
                root: props.classes.checkboxRoot,
                checked: props.classes.checked
              }}
            />
            Operator
          </MenuItem>
          <MenuItem value={UserRoles.SUPERVISOR}>
            <Checkbox
              checked={props.values.role === UserRoles.SUPERVISOR}
              tabIndex={-1}
              disableRipple
              classes={{
                root: props.classes.checkboxRoot,
                checked: props.classes.checked
              }}
            />
            Supervisor
          </MenuItem>
        </Select>
      </FormControl>

      <FormControl className={props.classes.textField}>
        <InputLabel htmlFor="select-multiple" shrink={true}>
          Status
        </InputLabel>
        <Select
          id="status"
          value={props.values.status}
          className={props.classes.textField}
          onChange={props.handleChange}
          margin="none"
          name="status"
          renderValue={() => (
            <Typography className={props.classes.textInput}>
              {props.values.status}
            </Typography>
          )}
        >
          <MenuItem value={'active'}>
            <Checkbox
              checked={props.values.status === 'active'}
              tabIndex={-1}
              disableRipple
              classes={{
                root: props.classes.checkboxRoot,
                checked: props.classes.checked
              }}
            />
            Active
          </MenuItem>
          <MenuItem value={'inactive'}>
            <Checkbox
              checked={props.values.status === 'inactive'}
              tabIndex={-1}
              disableRipple
              classes={{
                root: props.classes.checkboxRoot,
                checked: props.classes.checked
              }}
            />
            Inactive
          </MenuItem>
        </Select>
      </FormControl>
      <FormControl
        className={props.classes.textField}
        error={Boolean(props.errors.screens && props.touched.screens)}
      >
        <InputLabel htmlFor="select-multiple" shrink={true}>
          Screens
        </InputLabel>
        <Select
          multiple
          id="screens"
          value={props.values.screens}
          className={props.classes.textField}
          onChange={props.handleChange}
          margin="none"
          name="screens"
          renderValue={() => (
            <Typography className={props.classes.textInput}>
              {props.values.screens
                .map(screenId => {
                  return props.screens
                    .filter(screen => {
                      if (screen.value === screenId) {
                        return true;
                      } else {
                        return false;
                      }
                    })
                    .map(screen => screen.label);
                })
                .join(' , ')}
            </Typography>
          )}
        >
          {props.screens.map(screen => {
            return (
              <MenuItem key={screen.value} value={screen.value}>
                <Checkbox
                  checked={props.values.screens.indexOf(screen.value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  classes={{
                    root: props.classes.checkboxRoot,
                    checked: props.classes.checked
                  }}
                />
                {screen.label}
              </MenuItem>
            );
          })}
        </Select>
        <FormHelperText>
          {props.errors.screens &&
            props.touched.screens &&
            String(props.errors.screens)}
        </FormHelperText>
      </FormControl>
      <MyButton
        btnType={'yellow'}
        className={props.classes.textField}
        type="submit"
        disabled={props.isSubmitting}
      >
        Save
      </MyButton>
    </form>
  </div>
);

const UserForm = withFormik<IFormProps, IFormValues>({
  mapPropsToValues: props => {
    return {
      userId: props.user ? props.user.userId : '',
      name: props.user ? props.user.name : '',
      email: props.user ? props.user.email : '',
      role: props.user ? props.user.role : '',
      status: props.user
        ? props.user.status === true
          ? 'active'
          : 'inactive'
        : 'active',
      screens: props.user ? props.user.screens : [],
      // jiraUserName: props.user ? props.user.jiraUserName : ''
    };
  },
  validationSchema: Yup.object().shape({
    name: Yup.string().required('*Required'),
    // jiraUserName: Yup.string().required("*Required"),
    email: Yup.string()
      .email('Invalid email address!')
      .required('*Required')
    // screens: Yup.array().max(1)
  }),
  validate: () => {
    const errors: any = {};
    // if (values.role === "operator" && values.screens.length > 1) {
    //   errors["screens"] = "Cannot assign multiple screens to the operator!";
    // }
    // for (let u of props.users) {
    //   if (
    //     u.role === "supervisor" &&
    //     values.role == "supervisor" &&
    //     u.email !== values.email &&
    //     u.screens.filter(value => -1 !== values.screens.indexOf(value)).length >
    //       0
    //   ) {
    //     errors["screens"] = "Cannot assign same screen to multiple supervisor!";
    //   }
    // }

    return errors;
  },

  handleSubmit: (values, { props }) => {
    props.onSubmit(values);
  }
})(InnerForm);

export default withStyles(styles)(UserForm);
